import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AccountActionDialog, type AccountTarget } from './AccountActionDialog';
import { createSubmitLock } from './action-model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ApiError, deletePlayerAccount } from '@/lib/api';

export function DeleteAccountDialog({ target, onClose, onDeleted }: {
  target: AccountTarget; onClose: () => void; onDeleted: () => void;
}) {
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const submitting = useRef(createSubmitLock());
  const mutation = useMutation({
    mutationFn: (captured: { id: string; reason: string }) => deletePlayerAccount(captured.id, captured.reason),
    onSettled: () => submitting.current.end(),
  });
  return <AccountActionDialog title="Delete account permanently" target={target}
    scope="Permanent deletion from the backend and legacy PlayFab. Use only for a player who requested a complete restart."
    pending={mutation.isPending} changed={false} onClose={onClose}>
    <Alert variant="destructive" className="border-red-500/50 bg-red-950/30 text-red-200"><AlertDescription>
      Deletes EC2 cloud saves, recovery data, backend credentials, and all backend login links (Google, Apple, Discord, email and device).
      Deletes only the EC2 player data in legacy PlayFab. The shared PlayFab account, its login links, and Epic Conquest 1 are preserved. This cannot be undone.
      The player's device save is not erased remotely; they must clear app data before starting over.
    </AlertDescription></Alert>
    <p className="text-sm text-muted-foreground">PlayFab cleanup may take time. Backend access is blocked immediately.
      Purchase transactions remain anonymously recorded to prevent receipt reuse; moderator deletion receipts are retained.</p>
    <label className="block space-y-2 text-sm">Reason for deletion (player request)
      <Input value={reason} maxLength={1000} onChange={e => setReason(e.target.value)} disabled={mutation.isPending || mutation.isSuccess} />
    </label>
    <label className="block space-y-2 text-sm">Type the exact account ID to confirm
      <Input value={confirmation} autoComplete="off" onChange={e => setConfirmation(e.target.value)} disabled={mutation.isPending || mutation.isSuccess} />
    </label>
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} disabled={mutation.isPending || mutation.isSuccess} />
      The player requested permanent EC2 deletion and will clear EC2 app data before creating a fresh backend account.
    </label>
    <Button variant="destructive" disabled={!acknowledged || confirmation !== target.id || reason.trim().length < 3 || mutation.isPending || mutation.isSuccess}
      onClick={() => { if (submitting.current.begin()) mutation.mutate({ id: target.id, reason: reason.trim() }); }}>
      {mutation.isPending ? 'Deleting…' : 'Delete account permanently'}
    </Button>
    {mutation.error && <Alert variant="destructive"><AlertDescription>
      {mutation.error instanceof ApiError && mutation.error.message === 'deletion_incomplete_retry'
        ? 'Deletion is incomplete. Account access is blocked. Press Delete account permanently again to finish cleanup.'
        : mutation.error instanceof ApiError && mutation.error.message === 'deletion_in_progress'
          ? 'Another deletion is running. Wait a moment, then retry.' : String(mutation.error)}
    </AlertDescription></Alert>}
    {mutation.isSuccess && <Alert><AlertDescription>EC2 backend account deleted and all backend login links removed.
      {mutation.data.legacyDeletionQueued && ' Legacy EC2 PlayFab data deletion has been queued; cleanup may take several minutes. The shared account and EC1 are preserved.'}
      <Button className="mt-3 block" onClick={onDeleted}>Return to accounts</Button>
    </AlertDescription></Alert>}
  </AccountActionDialog>;
}
